import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { SERVICE_TYPES } from '../leasing/maintenanceShared';

const EMPTY = { customer_id: '', license_plate: '', vehicle: '', service_type: '', date: new Date().toLocaleDateString('sv-SE'), time: '', notes: '' };

export const AppointmentDialog = ({ open, onOpenChange, el, customers, appointment, onSaved }) => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (open) setForm(appointment ? { ...EMPTY, ...appointment } : EMPTY); }, [open, appointment]);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const valid = form.customer_id && form.license_plate.trim() && form.service_type && form.date;

    const save = async () => {
        setSaving(true);
        try {
            const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k] || '']));
            if (appointment) await api.put(`/workshop/appointments/${appointment.id}`, body);
            else await api.post('/workshop/appointments', body);
            toast.success(el ? 'Αποθηκεύτηκε' : 'Saved');
            onOpenChange(false);
            onSaved();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white max-w-lg" data-testid="appointment-dialog">
                <DialogHeader><DialogTitle>{appointment ? (el ? 'Επεξεργασία ραντεβού' : 'Edit appointment') : (el ? 'Νέο ραντεβού' : 'New appointment')}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1">
                        <Label>{el ? 'Πελάτης' : 'Customer'}</Label>
                        <Select value={form.customer_id} onValueChange={(v) => set('customer_id', v)}>
                            <SelectTrigger data-testid="appt-customer-select"><SelectValue placeholder={customers.length ? (el ? 'Επιλέξτε πελάτη' : 'Select customer') : (el ? 'Προσθέστε πρώτα πελάτη' : 'Add a customer first')} /></SelectTrigger>
                            <SelectContent className="bg-white">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1"><Label>{el ? 'Πινακίδα' : 'Plate'}</Label><Input value={form.license_plate} onChange={(e) => set('license_plate', e.target.value)} data-testid="appt-plate" /></div>
                        <div className="space-y-1"><Label>{el ? 'Όχημα (μάρκα/μοντέλο)' : 'Vehicle'}</Label><Input value={form.vehicle} onChange={(e) => set('vehicle', e.target.value)} data-testid="appt-vehicle" /></div>
                    </div>
                    <div className="space-y-1">
                        <Label>{el ? 'Εργασία' : 'Service'}</Label>
                        <Select value={form.service_type} onValueChange={(v) => set('service_type', v)}>
                            <SelectTrigger data-testid="appt-service-select"><SelectValue placeholder={el ? 'Επιλέξτε' : 'Select'} /></SelectTrigger>
                            <SelectContent className="bg-white">{SERVICE_TYPES.map((t) => <SelectItem key={t.el} value={el ? t.el : t.en}>{el ? t.el : t.en}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1"><Label>{el ? 'Ημερομηνία' : 'Date'}</Label><Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} data-testid="appt-date" /></div>
                        <div className="space-y-1"><Label>{el ? 'Ώρα' : 'Time'}</Label><Input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} data-testid="appt-time" /></div>
                    </div>
                    <div className="space-y-1"><Label>{el ? 'Σημειώσεις' : 'Notes'}</Label><Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="appt-notes" /></div>
                    <Button onClick={save} disabled={saving || !valid} className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-sm" data-testid="appt-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (el ? 'Αποθήκευση' : 'Save')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
